// src/components/books/BookCreator.jsx - VISUAL REDESIGN ONLY (All functionality preserved)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Plus, Building, Download, AlertCircle, CheckCircle, Sparkles, ArrowRight, 
         BookOpen
} from 'lucide-react';
import { csvService } from '../../services/csvService';
import { firestoreService } from '../../services/firestoreService';
import { useAuth } from '../../contexts/AuthContext';
import { TemplateSelector } from '../templates/TemplateSelector';
import { catalogService } from '../../services/catalogService';

export const BookCreator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creationMode, setCreationMode] = useState('template');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [importing, setImporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleCSVImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('Starting CSV import...');
      const result = await csvService.importBookFromCSV(file, {
        published: false,
        templateType: selectedTemplate?.id || 'general',
        ownerId: user.uid
      });
      
      console.log('Book imported successfully:', result);
      setSuccess('Book imported successfully!');
      
      setTimeout(() => {
        navigate(`/books/${result.bookId}/edit`);
      }, 1000);

    } catch (error) {
      console.error('Import failed:', error);
      setError('Failed to import CSV: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  const createFromTemplate = async (template) => {
    if (!template) {
      setError('No template selected');
      return;
    }

    if (!user?.uid) {
      setError('You must be logged in to create a book');
      return;
    }

    setCreating(true);
    setError('');
    setSuccess('');

    try {
      console.log('Creating book from template:', template.name);
      console.log('User ID:', user.uid);

      // Step 1: Create book data
      const bookData = {
        botId: `book_${Date.now()}`,
        botName: `New ${template.name} Book`,
        botCategory: template.name,
        botTask: `Interactive ${template.name.toLowerCase()} book`,
        templateType: template.id,
        accessLevel: 'private',
        published: false,
        ownerId: user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        customStyling: {
          primaryColor: '#0795ff',
          backgroundColor: '#f6f5f5',
          headerColor: '#0795ff'
        },
        analytics: {
          views: 0,
          interactions: 0,
          completions: 0
        },
        
        // Add catalog-specific fields when template is catalog
        ...(template.id === 'catalog' && {
          gridColumns: 3,
          showItemsInModal: true,
          allowExternalLinks: true,
          itemCount: 0,
          coverImageUrl: '',
          catalogItems: []
        }),

        // Add album-specific fields when template is album
        ...(template.id === 'album' && {
          galleries: [],
          albumLayout: 'grid',
          albumTheme: 'modern'
        }),

        ...(template.id === 'requests' && {
          requestCategories: [],
          locations: [],
        }),

        // Add profile-specific fields when template is profile
        ...(template.id === 'profile' && {
          profileSections: [],
          profileLayout: 'modern',
          showSocials: true
        }),

        // Add event-specific fields when template is event
        ...(template.id === 'event' && {
          eventDate: null,
          eventLocation: '',
          eventSchedule: [],
          eventSpeakers: []
        }),

        // Add ecommerce-specific fields when template is ecommerce
        ...(template.id === 'ecommerce' && {
          products: [],
          categories: [],
          currency: 'USD',
          paymentMethods: []
        }),

        // Add booking-specific fields when template is booking
        ...(template.id === 'booking' && {
          services: [],
          bookingSettings: {
            timeSlotDuration: 30,
            bufferTime: 0,
            maxAdvanceBooking: 30
          }
        }),

        // Add institution-specific fields when template is institution
        ...(template.id === 'institution' && {
          institutionType: 'school',
          programs: [],
          facilities: [],
          admissions: {}
        }),

        // Add organisation-specific fields when template is organisation
        ...(template.id === 'organisation' && {
          organisationType: 'business',
          services: [],
          team: [],
          managementFeatures: {}
        }),

        ...(template.id === 'leitner' && {
          leitnerSettings: {
            showLogo: true,
            showTitle: true,
            showDescription: false,
            headerHeight: 80,
            logoHeight: 40,
            
            timerEnabled: true,
            defaultTimerMode: 'perQuestion',
            defaultTimePerQuestion: 60,
            defaultTimeForSession: 300,
            enableTourOnFirstVisit: true,
            showProgressBar: true,
            showBoxCounts: true,
            enableSoundEffects: false,
            
            allowTestCreation: true,
            enableTestHistory: true,
            showPerformanceStats: true,
            maxQuestionsPerTest: 50,
            
            allowSharing: true,
            enableAnalytics: true,
            customCSS: ''
          },
          questionCount: 0,
          box1Count: 0,
          box2Count: 0,
          box3Count: 0,
          box4Count: 0,
          box5Count: 0,
          testCount: 0
        }),

        ...(template.id === 'diary' && {
          diarySettings: {
            enabledFeatures: ['meetings', 'journal', 'contacts', 'notes', 'templates'],
            defaultView: 'DASHBOARD',
            defaultMeetingDuration: 60,
            enabledDomains: ['PERSONAL', 'WORK', 'HEALTH', 'FINANCE', 'LEARNING'],
            enabledContactCategories: ['WORK', 'FRIEND', 'FAMILY', 'SERVICE', 'OTHER'],
            theme: 'light',
            weekStartsOn: 'monday',
            timeFormat: '24h',
            enableNotifications: true,
            reminderDefault: 15,
            aiContext: '',
            aiPersonality: 'professional',
            enableAISuggestions: false,
            geminiApiKey: ''
          },
          styling: {
            primaryColor: '#6366f1',
            accentColor: '#8b5cf6',
            backgroundColor: '#f8fafc',
            headerColor: '#ffffff',
            accentColor: '#10b981',
            fontFamily: 'Inter, sans-serif'
          }
        }),

        ...(template.id === 'course' && {
          courseSettings: {
            platformName: `New ${template.name} Platform`,
            tagline: 'Learn, Grow, Succeed',
            description: 'Your learning platform description',
            enableCertificates: true,
            enableGamification: true,
            enableVirtualSessions: false,
            allowPublicCourses: true,
            requireEnrollment: true,
            paymentEnabled: false,
            currency: 'USD',
            primaryColor: '#3b82f6',
            accentColor: '#8b5cf6',
            backgroundColor: '#ffffff'
          },
          courseCount: 0,
          studentCount: 0,
          instructorCount: 0
        }),

        ...(template.id === 'websearch' && {
          websearchSettings: {
            defaultCategory: 'lead',
            enabledCategories: ['lead', 'supplier', 'tender'],
            resultsPerSearch: 10,
            groundingUrls: [],
            autoSaveResults: false,
            enableBulkImport: true,
            enableExport: true,
            enableEngagementTracking: true,
            headerTitle: 'Lead Scout',
            headerSubtitle: 'Find Your Next Opportunity'
          }
        })
      };

      console.log('Book data prepared:', bookData);

      const bookId = await firestoreService.createBook(bookData);
      console.log('Book created with ID:', bookId);

      const initialMessages = getTemplateMessages(template, bookId, bookData);
      console.log('Initial messages:', initialMessages);

      await firestoreService.batchCreateMessages(initialMessages);
      console.log('Messages created successfully');

      setSuccess('Book created successfully!');
      
      setTimeout(() => {
        navigate(`/books/${bookId}/edit`);
      }, 1000);

    } catch (error) {
      console.error('Creation failed:', error);
      setError('Failed to create book: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const getTemplateMessages = (template, bookId, bookData) => {
    const baseMessage = {
      messageId: '1',
      botId: bookData.botId,
      botName: bookData.botName,
      botCategory: bookData.botCategory,
      type: 'text',
      condition: '',
      section: 'Welcome',
      message: `Welcome to your new ${template.name} book! Click next to continue.`,
      userResponseType: '',
      variable: '',
      action: '',
      mediaUrl: '',
      integrationType: '',
      integrationParameter: '',
      accessLevel: '',
      authUsers: '',
      bookId,
      order: 0,
      status: 'active',
      createdAt: new Date()
    };

    // Add template-specific messages
    const templateMessages = [];
    
    switch (template.id) {
      case 'catalog':
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '1',
            type: 'catalog-header',
            section: 'Header',
            message: 'Catalog Header Configuration',
            order: 0
          },
          {
            ...baseMessage,
            messageId: '2',
            type: 'catalog-items',
            section: 'Items',
            message: 'Your catalog items will appear here. Use the Items Manager to add products.',
            order: 1
          }
        );
        break;
      
      case 'album':
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '1',
            type: 'album-header',
            section: 'Header',
            message: 'Album Header Configuration',
            order: 0
          },
          {
            ...baseMessage,
            messageId: '2',
            type: 'album-galleries',
            section: 'Galleries',
            message: 'Your photo and video galleries will appear here. Use the Gallery Manager to add media.',
            order: 1
          }
        );
        break;

      case 'education':
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '2',
            section: 'Introduction',
            message: 'What\'s your name?',
            type: 'form',
            userResponseType: 'text',
            variable: 'studentName',
            order: 1
          },
          {
            ...baseMessage,
            messageId: '3',
            section: 'Chapter 1',
            message: 'Hello {studentName}! Let\'s start learning.',
            order: 2
          }
        );
        break;

        case 'leitner':
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '1',
            type: 'leitner-header',
            section: 'Header',
            message: 'Leitner System Configuration',
            order: 0
          },
          {
            ...baseMessage,
            messageId: '2',
            type: 'leitner-system',
            section: 'Learning System',
            message: 'Your Leitner spaced repetition system. Use the Manager to add questions and create tests for learners.',
            order: 1
          }
        );
        break;

        case 'course':
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '1',
            type: 'course-hero',
            section: 'Hero',
            message: 'Course Platform Hero Section',
            order: 0
          },
          {
            ...baseMessage,
            messageId: '2',
            type: 'course-catalog',
            section: 'Courses',
            message: 'Browse and discover courses. Use the Course Manager to create courses.',
            order: 1
          }
        );
        break;

        case 'websearh':
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '1',
            type: 'webseach-hero',
            section: 'Hero',
            message: 'Websearch Platform Hero Section',
            order: 0
          },
          {
            ...baseMessage,
            messageId: '2',
            type: 'websearch-list',
            section: 'leads',
            message: 'Search leads and suppliers from the web and save them in your database',
            order: 1
          }
        );
        break;

        case 'diary':
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '1',
            type: 'diary-sidebar',
            section: 'Sidebar',
            message: 'ParrotPab Diary Section',
            order: 0
          },
          {
            ...baseMessage,
            messageId: '2',
            type: 'diary-list',
            section: 'contacts',
            message: 'Save contacts and dynamically call and send messages',
            order: 1
          }
        );
        break;

        case 'requests':
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '1',
            type: 'request-hero',
            section: 'Hero',
            message: 'Open Requests Platform Hero Section',
            order: 0
          },
          {
            ...baseMessage,
            messageId: '2',
            type: 'request-list',
            section: 'leads',
            message: 'Send request and receive offers from providers',
            order: 1
          }
        );
        break;

        case 'flipbook':
        bookData.pageCount = 0;
        bookData.pageWidth = 800;
        bookData.pageHeight = 1000;
        bookData.flipbookSettings = {
          enableSound: true,
          enableNarration: true,
          autoPlay: false,
          showPageNumbers: true,
          flipDuration: 1000,
          shadowOpacity: 0.5
        };
        
        templateMessages.push(
          {
            ...baseMessage,
            messageId: '1',
            type: 'flipbook-header',
            section: 'Header',
            message: 'Flipbook Header Configuration',
            order: 0
          },
          {
            ...baseMessage,
            messageId: '2',
            type: 'flipbook-pages',
            section: 'Pages',
            message: 'Upload a PDF to create your flipbook pages. You can add voice narration to each page.',
            order: 1
          }
        );
        break;

      default:
        templateMessages.push(baseMessage);
    }

    return templateMessages.length > 0 ? templateMessages : [baseMessage];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* ENHANCED HEADER */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 text-white py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Create Something Amazing</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Create Your Interactive Book</h1>
          <p className="text-xl text-white/90">Choose how you want to get started</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-12">
        {/* STATUS MESSAGES - PRESERVED EXACTLY */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-lg shadow-lg">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-800">Success!</h3>
                <div className="text-sm text-green-700">{success}</div>
              </div>
            </div>
          </div>
        )}

        {/* MODE SELECTOR - ENHANCED VISUALS ONLY */}
        <div className="mb-8 bg-white rounded-2xl shadow-lg p-6">
          <div className="flex gap-4">
            <button
              onClick={() => setCreationMode('template')}
              className={`flex-1 px-6 py-4 rounded-xl font-semibold transition-all duration-200 ${
                creationMode === 'template' 
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg scale-105' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Plus className="w-5 h-5 inline mr-2" />
              Create from Template
            </button>
            
            <button
              onClick={() => setCreationMode('csv')}
              className={`flex-1 px-6 py-4 rounded-xl font-semibold transition-all duration-200 ${
                creationMode === 'csv' 
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg scale-105' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Upload className="w-5 h-5 inline mr-2" />
              Import from CSV
            </button>
          </div>
        </div>

        {/* TEMPLATE MODE - PRESERVED FUNCTIONALITY */}
        {creationMode === 'template' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Choose a Template</h2>
              <p className="text-slate-600">Select a template that matches your content type</p>
            </div>
            
            <TemplateSelector onSelect={setSelectedTemplate} />
            
            {selectedTemplate && (
              <div className="mt-8 bg-white rounded-2xl shadow-xl p-8 border-2 border-purple-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      Selected: {selectedTemplate.name}
                    </h3>
                    <p className="text-slate-600 text-lg">{selectedTemplate.description}</p>
                  </div>
                </div>
                
                {/* Debug info - preserved from original */}
                {!user?.uid && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 font-medium">
                      ⚠️ You must be logged in to create a book.
                    </p>
                  </div>
                )}
                
                <button 
                  onClick={() => createFromTemplate(selectedTemplate)}
                  disabled={creating || !user?.uid}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all duration-200 hover:scale-105"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      Creating Your Book...
                    </>
                  ) : (
                    <>
                      <Plus className="w-6 h-6" />
                      Create {selectedTemplate.name} Book
                      <ArrowRight className="w-6 h-6" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* CSV MODE - PRESERVED FUNCTIONALITY */}
        {creationMode === 'csv' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Import from CSV</h2>
              <p className="text-slate-600">Upload your CSV file to quickly create a book</p>
            </div>

            {/* CSV Upload Area - ENHANCED VISUALS ONLY */}
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center border-2 border-dashed border-slate-300 hover:border-blue-400 transition-colors">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full mb-6">
                <Upload className="w-12 h-12 text-blue-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {importing ? 'Importing your book...' : 'Upload CSV File'}
              </h3>
              <p className="text-slate-600 mb-8 text-lg">
                {importing ? 'Please wait while we process your file' : 'Choose a CSV file in the standard format'}
              </p>
              
              <input
                type="file"
                accept=".csv"
                onChange={handleCSVImport}
                className="hidden"
                id="csv-upload"
                disabled={importing}
              />
              
              <label
                htmlFor="csv-upload"
                className={`inline-flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-lg cursor-pointer transition-all duration-200 ${
                  importing 
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:shadow-xl hover:scale-105'
                }`}
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <FileText className="w-6 h-6" />
                    Choose CSV File
                    <ArrowRight className="w-6 h-6" />
                  </>
                )}
              </label>
            </div>

            {/* CSV Format Help - PRESERVED FROM ORIGINAL */}
            <div className="mt-8 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
                  <FileText className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold mb-4">Expected CSV Format</h4>
                  <div className="bg-slate-950 rounded-lg p-4 mb-6 overflow-x-auto">
                    <code className="text-sm text-green-400 font-mono">
                      messageId,botId,botName,botCategory,type,Condition,Section,message,userResponseType,variable,Action,mediaUrl,integrationType,integrationParameter,AccessLevel,authUsers
                    </code>
                  </div>
                  
                  <button
                    onClick={downloadCSVTemplate}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    Download CSV Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function - PRESERVED EXACTLY FROM ORIGINAL
const downloadCSVTemplate = () => {
  const template = `messageId,botId,botName,botCategory,type,Condition,Section,message,userResponseType,variable,Action,mediaUrl,integrationType,integrationParameter,AccessLevel,authUsers
1,sample_book,Sample Book,Education,text,,Welcome,Welcome to your interactive book!,,,,,,,,
2,sample_book,Sample Book,Education,form,,Introduction,What's your name?,text,userName,,,,,,
3,sample_book,Sample Book,Education,text,,Chapter 1,Hello {userName}! Let's get started.,,,,,,,,`;

  const blob = new Blob([template], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'book_template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};